import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import AuthLayout from '../../../components/layouts/AuthLayout';
import Card from '../../../components/common/Card';
import OTPInput from '../../../components/seller/OTPInput';
import Button from '../../../components/common/Button';
import { useToast } from '../../../components/common/Toast';
import { sellerService } from '../../../services/seller.service';
import { setCredentials, updateUser, selectUser } from '../../../store/slices/authSlice';
import { theme } from '../../../config/theme';
import { maskPhone } from '../../../utils/helpers';
import { OTP } from '../../../config/constants';

const VerifyOTP = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const toast = useToast();
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [countdown, setCountdown] = useState(OTP.EXPIRY_MINUTES * 60);
  const [resendCooldown, setResendCooldown] = useState(0);
  
  const tempData = JSON.parse(sessionStorage.getItem('seller_temp_data') || '{}');
  const reduxUser = useSelector(selectUser);
  // Fallback to Redux store phone (for post-login access where sessionStorage may be empty)
  const phone = tempData.phone || reduxUser?.phone || '';

  useEffect(() => {
    if (!phone) navigate('/register');
  }, [phone, navigate]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleOTPComplete = async (otpValue) => {
    setOtp(otpValue);
    await handleVerify(otpValue);
  };

  const handleVerify = async (otpValue = otp) => {
    if (otpValue.length !== OTP.LENGTH) {
      toast.error('Please enter complete OTP');
      return;
    }

    setLoading(true);
    try {
      const response = await sellerService.verifyOTP(phone, otpValue);
      
      dispatch(setCredentials(response.data));
      // Update Redux user state to reflect verified phone
      dispatch(updateUser({ is_active: true, phone_verified: true }));
      sessionStorage.removeItem('seller_temp_data');
      
      toast.success('Phone verified successfully!');
      navigate('/seller/onboarding/status');
    } catch (error) {
      toast.error(error.response?.data?.message || 'OTP verification failed. Please try again.');
      setLoading(false);
      setOtp('');
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    try {
      await sellerService.resendOTP(phone, 'phone');
      setCountdown(OTP.EXPIRY_MINUTES * 60);
      setResendCooldown(OTP.RESEND_COOLDOWN);
      toast.success('New OTP sent to your phone!');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to resend OTP');
    } finally {
      setResendLoading(false);
    }
  };

  if (!phone) return null;

  return (
    <AuthLayout>
      <Card padding="xl" style={{ width: '100%', maxWidth: '500px' }}>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ fontSize: theme.typography.fontSize['2xl'], fontWeight: theme.typography.fontWeight.bold, marginBottom: theme.spacing.md }}>
            Verify Phone Number
          </h2>
          <p style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary, marginBottom: theme.spacing.xl }}>
            OTP sent to: +91 {maskPhone(phone)}
          </p>

          <OTPInput length={OTP.LENGTH} onChange={setOtp} onComplete={handleOTPComplete} />

          <div style={{ fontSize: theme.typography.fontSize.lg, fontWeight: theme.typography.fontWeight.semibold, color: countdown < 60 ? theme.colors.error : theme.colors.text.primary, marginTop: theme.spacing.lg, marginBottom: theme.spacing.lg }}>
            Expires in: {formatTime(countdown)}
          </div>

          <div style={{ display: 'flex', gap: theme.spacing.md }}>
            <Button onClick={() => handleVerify()} loading={loading} disabled={loading || otp.length !== OTP.LENGTH} style={{ flex: 1 }}>
              Verify OTP
            </Button>
            <Button variant="secondary" onClick={handleResend} loading={resendLoading} disabled={resendLoading || resendCooldown > 0} style={{ flex: 1 }}>
              {resendCooldown > 0 ? `Resend (${resendCooldown}s)` : 'Resend OTP'}
            </Button>
          </div>
        </div>
      </Card>
    </AuthLayout>
  );
};

export default VerifyOTP;